import PropTypes from "prop-types";
import Range from "rc-slider/lib/Range";
import React from "react";

import { audienceProps, segmentProps } from "./audienceProps";
import { className, styles } from "./audienceStyles";
import "rc-slider/assets/index.css?global";

export default class ContinuousAudienceSegmentSelector extends React.PureComponent {
    static propTypes = {
        isReadOnly: PropTypes.bool.isRequired,
        marks: audienceProps.marks.isRequired,
        segments: PropTypes.arrayOf(
            PropTypes.shape({
                ...segmentProps,
                minMark: segmentProps.minMark.isRequired,
                maxMark: segmentProps.maxMark.isRequired
            })
        ).isRequired,
        onChange: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);

        this.onChange = this.onChange.bind(this);
        this.onAfterChange = this.onAfterChange.bind(this);

        this.state = {
            dragMinMax: null
        };
    }

    onAfterChange([min, max]) {
        if (this.props.isReadOnly) {
            return;
        }

        min = Math.round(min);
        max = Math.round(max);
        const [oldMin, oldMax] = this.minMax();

        if (min === oldMin && max === oldMax) {
            // The slider triggered a change event, but nothing interesting happened. We don't have to trigger an event,
            // but we do need to re-render to make sure the handles aren't between marks on the slider.
            return this.setState({ dragMinMax: [oldMin, oldMax] });
        }

        const adjustMin = oldMin < min ? -1 : 0;
        const adjustMax = oldMax > max ? 1 : 0;

        const isMarkSelected = segment =>
            min <= segment.minMark && max >= segment.maxMark;

        let selectedSegments;
        while (true) {
            selectedSegments = this.props.segments.filter(isMarkSelected);
            if (selectedSegments.length !== 0) {
                break;
            }

            // set up for the next iteration... if we got 0 selected segments this time, we'll slide the handles
            // back/forward by 1 until we get some segments
            const nextMin = Math.max(0, min + adjustMin);
            const nextMax = Math.min(
                this.props.marks.length - 1,
                max + adjustMax
            );

            // We walked our handles out to the edge but still didn't find a segment. This indicates a configuration
            // error in the component. It's not clear what to do here, so I'll just reset to the initial state and
            // bail out.
            if (nextMin === min && nextMax === max) {
                return this.setState({ dragMinMax: [oldMin, oldMax] });
            }

            min = nextMin;
            max = nextMax;
        }

        this.props.onChange(selectedSegments).then(() => {
            this.setState({ dragMinMax: null });
        });
    }

    onChange(minMax) {
        this.setState({ dragMinMax: minMax });
    }

    minMax() {
        const selectedSegments = this.props.segments.filter(
            segment => segment.isSelected
        );
        const min = Math.min(...selectedSegments.map(seg => seg.minMark));
        const max = Math.max(...selectedSegments.map(seg => seg.maxMark));
        return [min, max];
    }

    render() {
        const marks = this.props.marks.reduce((marks, mark, index) => {
            // stupid PropTypes insists that an array is not an object even
            // though it totally works. This is reduce call is here to
            // avoid the console warning
            marks[index] = mark;
            return marks;
        }, {});

        const sliderValue = this.state.dragMinMax || this.minMax();

        return (
            <div
                {...className("segmentSelection", "continuousSegmentSelection")}
            >
                <Range
                    className={styles.continuousSegmentsSlider}
                    disabled={this.props.isReadOnly}
                    allowCross={true}
                    value={sliderValue}
                    dots={false}
                    marks={marks}
                    min={0}
                    max={this.props.marks.length - 1}
                    step={0.01}
                    onChange={this.onChange}
                    onAfterChange={this.onAfterChange}
                />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/CreateTest/ContinuousAudienceSegmentSelector.js